import { DATE_FILTER_OPTIONS } from 'features/search/enums'
import { MAX_PRICE_IN_CENTS } from 'features/search/helpers/reducer.helpers'
import { NUMERIC_FILTERS_ENUM } from 'libs/algolia/enums/facetsEnums'
import {
  computeTimeRangeFromHoursToSeconds,
  TIMESTAMP,
} from 'libs/algolia/fetchAlgolia/buildAlgoliaParameters/helpers/datetime/time'
import { FiltersArray, SearchQueryParameters } from 'libs/algolia/types'
import { convertCentsToEuros } from 'libs/parsers/pricesConversion'
import { NoNullProperties, Range } from 'libs/typesUtils/typeHelpers'

export const buildOfferLast30DaysBookings = (
  minBookingsThreshold: number | undefined
): FiltersArray[0] | undefined => {
  if (!minBookingsThreshold) return undefined
  return [`${NUMERIC_FILTERS_ENUM.OFFER_LAST_30_DAYS_BOOKINGS} >= ${minBookingsThreshold}`]
}

export const buildOfferPriceRangePredicate = (
  minPrice = 0,
  maxPrice = convertCentsToEuros(MAX_PRICE_IN_CENTS)
): FiltersArray[0] => {
  const min = Math.max(0, minPrice)
  const max = Math.min(convertCentsToEuros(MAX_PRICE_IN_CENTS), maxPrice)
  return [`${NUMERIC_FILTERS_ENUM.OFFER_PRICES}: ${min} TO ${max}`]
}

export const buildDatePredicate = ({
  date,
  timeRange,
}: Pick<SearchQueryParameters, 'date' | 'timeRange'>): FiltersArray[0] | undefined => {
  if (date && timeRange) return buildDateAndTimePredicate({ date, timeRange })
  if (date) return buildDateOnlyPredicate(date)
  if (timeRange) return buildTimeOnlyPredicate(timeRange)
  return undefined
}

export const buildHomepageDatePredicate = (
  {
    beginningDatetime,
    endingDatetime,
  }: Pick<SearchQueryParameters, 'beginningDatetime' | 'endingDatetime'>,
  isUsedFromSearch?: boolean
): undefined | FiltersArray[0] => {
  if (!(beginningDatetime || endingDatetime)) return undefined
  const formattedBeginningDatetime = beginningDatetime ? new Date(beginningDatetime) : undefined
  const formattedEndingDatetime = endingDatetime ? new Date(endingDatetime) : undefined

  if (formattedBeginningDatetime && !formattedEndingDatetime) {
    const beginningTimestamp = TIMESTAMP.getFromDate(formattedBeginningDatetime)
    const endOfBeginningTimestamp = TIMESTAMP.getLastOfDate(formattedBeginningDatetime)
    return isUsedFromSearch
      ? [`${NUMERIC_FILTERS_ENUM.OFFER_DATES}: ${beginningTimestamp} TO ${endOfBeginningTimestamp}`]
      : [`${NUMERIC_FILTERS_ENUM.OFFER_DATES} >= ${beginningTimestamp}`]
  }

  if (!formattedBeginningDatetime && formattedEndingDatetime) {
    const endingTimestamp = TIMESTAMP.getFromDate(formattedEndingDatetime)
    return [`${NUMERIC_FILTERS_ENUM.OFFER_DATES} <= ${endingTimestamp}`]
  }

  if (formattedBeginningDatetime && formattedEndingDatetime) {
    const beginningTimestamp = TIMESTAMP.getFromDate(formattedBeginningDatetime)
    const endingTimestamp = TIMESTAMP.getFromDate(formattedEndingDatetime)
    return [getDatePredicate(beginningTimestamp, endingTimestamp)]
  }

  return undefined
}

export const buildTimeOnlyPredicate = (timeRange: Range<number>): FiltersArray[0] => {
  const timeRangeInSeconds = computeTimeRangeFromHoursToSeconds(timeRange)
  return [`${NUMERIC_FILTERS_ENUM.OFFER_TIMES}: ${timeRangeInSeconds.join(' TO ')}`]
}

export const buildDateAndTimePredicate = ({
  date,
  timeRange,
}: NoNullProperties<
  Required<Pick<SearchQueryParameters, 'date' | 'timeRange'>>
>): FiltersArray[0] => {
  let dateFilter: Range<number>[]
  // To be sure to have a value in Date format
  const selectedDate = new Date(date.selectedDate)
  switch (date.option) {
    case DATE_FILTER_OPTIONS.CURRENT_WEEK:
      dateFilter = TIMESTAMP.WEEK.getAllFromTimeRangeAndDate(selectedDate, timeRange)
      break
    case DATE_FILTER_OPTIONS.CURRENT_WEEK_END:
      dateFilter = TIMESTAMP.WEEK_END.getAllFromTimeRangeAndDate(selectedDate, timeRange)
      break
    default:
      dateFilter = [TIMESTAMP.getAllFromTimeRangeAndDate(selectedDate, timeRange)]
  }

  return dateFilter.map((timestampsRangeForADay) => getDatePredicate(...timestampsRangeForADay))
}

export const buildDateOnlyPredicate = (
  date: Exclude<SearchQueryParameters['date'], null | undefined>
): FiltersArray[0] => {
  let beginningDate, endingDate: number
  // To be sure to have a value in Date format
  const selectedDate = new Date(date.selectedDate)
  switch (date.option) {
    case DATE_FILTER_OPTIONS.TODAY:
      beginningDate = TIMESTAMP.getFromDate(selectedDate)
      endingDate = TIMESTAMP.getLastOfDate(selectedDate)
      break
    case DATE_FILTER_OPTIONS.CURRENT_WEEK:
      beginningDate = TIMESTAMP.getFromDate(selectedDate)
      endingDate = TIMESTAMP.WEEK.getLastFromDate(selectedDate)
      break
    case DATE_FILTER_OPTIONS.CURRENT_WEEK_END:
      beginningDate = TIMESTAMP.WEEK_END.getFirstFromDate(selectedDate)
      endingDate = TIMESTAMP.WEEK.getLastFromDate(selectedDate)
      break
    case DATE_FILTER_OPTIONS.USER_PICK:
      beginningDate = TIMESTAMP.getFirstOfDate(selectedDate)
      endingDate = TIMESTAMP.getLastOfDate(selectedDate)
      break
  }
  return [getDatePredicate(beginningDate, endingDate)]
}

export const getDatePredicate = (lowerDate: number, higherDate: number): string =>
  `${NUMERIC_FILTERS_ENUM.OFFER_DATES}: ${lowerDate} TO ${higherDate}`
